import {Mutations} from '../../api/queries';
import {useTextInputState} from '../../hooks';
import CustomerPicker from '../CustomerPicker';
import DatePicker from '../DatePicker';
import Layout from '../Layout';
import MoneyInput from '../forms/MoneyInput';
import ProjectStatusPicker from '../forms/ProjectStatusPicker';
import {Box, FormField, Icon, Input, LoaderButton} from '@sproutsocial/racine';
import {useMutation} from '@tanstack/react-query';
import {DateTime} from 'luxon';
import {useCallback, useMemo, useState} from 'react';
import {ProjectStatus} from 'shared/src/models';

const NewProject = () => {
	const [name, onNameChange] = useTextInputState();
	const [customerId, onCustomerIdChange] = useState<string | null>(null);
	const [description, onDescriptionChange] = useTextInputState();
	const [startDate, onStartDateChange] = useState<DateTime>(DateTime.now());
	const [cost, onCostChange] = useState<number | null>(null);
	const [deposit, onDepositChange] = useState<number | null>(null);
	const [status, onStatusChange] = useState<ProjectStatus>(ProjectStatus.Upcoming);

	const isValid = useMemo<boolean>(() => {
		if (!name.trim() || !description.trim()) {
			return false;
		}

		if (!customerId) {
			return false;
		}

		if (startDate < DateTime.fromISO('2020-01-01')) {
			return false;
		}

		return true;
	}, [name, description, customerId, startDate]);

	const {mutateAsync: createProject, isPending: isCreating} = useMutation(
		Mutations.createProject(),
	);

	const onCreate = useCallback(async () => {
		const createdProject = await createProject({
			project: {
				name: name.trim(),
				description: description.trim(),
				startDate,
				endDate: null,
				cost,
				deposit,
				customer: customerId!,
				invoice: null,
				status,
				hoursLogged: null,
			},
		});

		window.location.href = `/projects/${createdProject.id}`;
	}, [createProject, name, description, startDate, customerId, cost, deposit, status]);

	return (
		<Layout>
			<Layout.Header title='New Project'>
				<LoaderButton
					appearance='primary'
					isLoading={isCreating}
					onClick={onCreate}
					disabled={!isValid}
				>
					<Icon name='plus' mr='space.300' /> Create
				</LoaderButton>
			</Layout.Header>

			<Layout.Body>
				<Box maxWidth={300}>
					<FormField label='Status'>
						{(props) => (
							<ProjectStatusPicker {...props} status={status} onChange={onStatusChange} />
						)}
					</FormField>
					<FormField label='Name'>
						{(props) => (
							<Input {...props} name='projectName' value={name} onChange={onNameChange} required />
						)}
					</FormField>
					<FormField label='Customer'>
						{(props) => (
							<CustomerPicker
								{...props}
								selectedCustomerId={customerId}
								onChange={onCustomerIdChange}
							/>
						)}
					</FormField>
				</Box>
				<Box maxWidth={600}>
					<FormField label='Description'>
						{(props) => (
							<Input
								{...props}
								name='description'
								value={description}
								onChange={onDescriptionChange}
								required
							/>
						)}
					</FormField>
				</Box>
				<Box maxWidth={300}>
					<FormField label='Start Date'>
						{(props) => <DatePicker {...props} value={startDate} onChange={onStartDateChange} />}
					</FormField>
					<FormField label='Cost'>
						{(props) => (
							<MoneyInput {...props} name='cost' value={cost} onChange={onCostChange} required />
						)}
					</FormField>
					<FormField label='Deposit'>
						{(props) => (
							<MoneyInput
								{...props}
								name='deposit'
								value={deposit}
								onChange={onDepositChange}
								required
							/>
						)}
					</FormField>
				</Box>
			</Layout.Body>
		</Layout>
	);
};

export default NewProject;
